import { useState, useEffect, useRef, createContext, useContext } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";

// Configure axios to send credentials (cookies) with all requests
axios.defaults.withCredentials = true;    

// Pages
import Login from "./pages/Login";
import InformacionGeneral from "./pages/InformacionGeneral";
import RecursosCompartidos from "./pages/RecursosCompartidos";
import General from "./pages/General";
import Operaciones from "./pages/Operaciones";
import Talento from "./pages/Talento";
import Academia from "./pages/Academia";
import Comunicacion from "./pages/Comunicacion";
import Finanzas from "./pages/Finanzas";
import Informacion from "./pages/Informacion";
import Tecnologia from "./pages/Tecnologia";
import WCALearn from "./pages/WCALearn";
import WCANetwork from "./pages/WCANetwork";
import WCALabs from "./pages/WCALabs";
import WCAImpact from "./pages/WCAImpact";
import AdminPanel from "./pages/AdminPanel";
import Permisos from "./pages/Permisos";
import PoliticaPrivacidad from "./pages/PoliticaPrivacidad";
import TerminosServicio from "./pages/TerminosServicio";
import Layout from "./components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
if (!process.env.REACT_APP_BACKEND_URL) {
  console.warn('⚠️ REACT_APP_BACKEND_URL no está definido. Usando valor por defecto: http://localhost:8000');
}
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  // Load user from localStorage on init for session persistence
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('wca_user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (error) {
      console.error("Error loading user from localStorage:", error);
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      const userData = response.data;
      setUser(userData);
      // Persist user data to localStorage
      try {
        localStorage.setItem('wca_user', JSON.stringify(userData));
      } catch (error) {
        console.error("Error saving user to localStorage:", error);
      }
    } catch (error) {
      // If auth check fails, try to use cached user data if available
      // This helps with session persistence across tabs
      if (error.response?.status === 401) {
        // For 401 errors, try to keep cached user data temporarily
        // This allows the UI to work while the session is being refreshed
        const cachedUser = localStorage.getItem('wca_user');
        if (cachedUser && window.location.pathname === '/pending') {
          // Keep cached user on pending page
          try {
            const parsed = JSON.parse(cachedUser);
            setUser(parsed);
          } catch (e) {
            // If parsing fails, clear everything
            localStorage.removeItem('wca_user');
            if (window.location.pathname !== '/pending') {
              setUser(null);
            }
          }
        } else {
          // Clear localStorage and user if not on pending page
          try {
            localStorage.removeItem('wca_user');
          } catch (e) {
            console.error("Error clearing localStorage:", e);
          }
          if (window.location.pathname !== '/pending') {
            setUser(null);
          }
        }
      } else {
        // For other errors, clear everything
        setUser(null);
        try {
          localStorage.removeItem('wca_user');
        } catch (e) {
          console.error("Error clearing localStorage:", e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (sessionToken, userData) => {
    // Cookie is now set by the backend, but we keep this as fallback
    // The backend sets the cookie with proper CORS settings
    if (sessionToken) {
      // Try to set cookie as fallback (may not work due to CORS, but backend should handle it)
      try {
        document.cookie = `session_token=${sessionToken}; path=/; secure; samesite=none; max-age=${7 * 24 * 60 * 60}`;
      } catch (error) {
        console.warn("Could not set cookie from frontend (expected if CORS is configured):", error);
      }
    }
    setUser(userData);
    // Persist user data to localStorage
    try {
      localStorage.setItem('wca_user', JSON.stringify(userData));
    } catch (error) {
      console.error("Error saving user to localStorage:", error);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    
    // Clear session cookie
    document.cookie = "session_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    
    // Clear localStorage
    try {
      localStorage.removeItem('wca_user');
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }
    
    // Sign out from Firebase if available
    try {
      const { auth } = await import("./firebase");
      if (auth) {
        const { signOut } = await import("firebase/auth");
        await signOut(auth);
      }
    } catch (error) {
      console.error("Firebase logout error:", error);
    }
    
    setUser(null);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component (removed)

// Protected Route Component
const ProtectedRoute = ({ children, allowPending = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-primary-50 to-secondary-50">
        <div className="glass-panel p-8 text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // If this is the pending route, allow it to render even without user
  if (allowPending && location.pathname === "/pending") {
    return children;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user account is active
  const isSuperAdmin = user?.is_super_admin || 
    user?.email === "contacto@ecosistemawca.com" || 
    user?.email === "ecosistemawca@gmail.com";

  // If user is not super admin and account is not active, redirect to pending page
  if (!isSuperAdmin && !user?.is_active) {
    return <Navigate to="/pending" replace />;
  }

  return children;
};

// Pending Access Page
const PendingAccess = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-primary-50/30 to-secondary-50/30 p-4">
        <div className="glass-panel p-6 sm:p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // If we have user data, check their status
  if (user) {
    const isSuperAdmin = user?.is_super_admin || 
      user?.email === "contacto@ecosistemawca.com" || 
      user?.email === "ecosistemawca@gmail.com";

    // If user is active or super admin, redirect to general
    if (isSuperAdmin || user?.is_active) {
      return <Navigate to="/general" replace />;
    }
  }

  // Show pending page even if user data is not available
  // This handles the case where auth check fails but user was redirected here

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-primary-50/30 to-secondary-50/30 p-4">
      <div className="glass-panel p-6 sm:p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600 sm:w-10 sm:h-10">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2">Cuenta Pendiente</h1>
        <p className="text-slate-600 mb-6 text-sm sm:text-base">
          Tu cuenta está registrada pero aún no ha sido activada por el administrador.
        </p>
        <div className="space-y-3">
          <a
            href="mailto:contacto@ecosistemawca.com"
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
            Contactar Administrador
          </a>
          <button onClick={handleLogout} className="btn-secondary w-full">
            Cerrar Sesión
          </button>
        </div>
        {user?.email && (
          <p className="text-xs sm:text-sm text-slate-400 mt-6">
            Email: {user.email}
          </p>
        )}
      </div>
    </div>
  );
};

// Module Access Check Component
const ModuleRoute = ({ moduleId, moduleType, children }) => {
  const { user } = useAuth();
  
  const isSuperAdmin = user?.is_super_admin || 
    user?.email === "contacto@ecosistemawca.com" || 
    user?.email === "ecosistemawca@gmail.com";
  
  if (isSuperAdmin || user?.role === "admin") {
    return children;
  }
  
  if (!user?.is_active) {
    return <Navigate to="/pending" replace />;
  }
  
  const hasAccess = moduleType === "direction" 
    ? user?.assigned_directions?.includes(moduleId)
    : user?.assigned_divisions?.includes(moduleId);
  
  if (!hasAccess) {
    return <Navigate to="/general" replace />;
  }
  
  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/politica-privacidad" element={<PoliticaPrivacidad />} />
      <Route path="/terminos-servicio" element={<TerminosServicio />} />
      <Route path="/permisos" element={
        <ProtectedRoute>
          <Permisos />
        </ProtectedRoute>
      } />
      <Route path="/pending" element={
        <ProtectedRoute allowPending={true}>
          <PendingAccess />
        </ProtectedRoute>
      } />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/general" replace />} />
        <Route path="general" element={<InformacionGeneral />} />
        <Route path="recursos" element={<RecursosCompartidos />} />
        <Route path="admin" element={<AdminPanel />} />
        <Route path="d-general" element={
          <ModuleRoute moduleId="general" moduleType="direction">
            <General />
          </ModuleRoute>
        } />
        <Route path="d-operaciones" element={
          <ModuleRoute moduleId="operaciones" moduleType="direction">
            <Operaciones />
          </ModuleRoute>
        } />
        <Route path="d-talento" element={
          <ModuleRoute moduleId="talento" moduleType="direction">
            <Talento />
          </ModuleRoute>
        } />
        <Route path="d-academia" element={
          <ModuleRoute moduleId="academia" moduleType="direction">
            <Academia />
          </ModuleRoute>
        } />
        <Route path="d-comunicacion" element={
          <ModuleRoute moduleId="comunicacion" moduleType="direction">
            <Comunicacion />
          </ModuleRoute>
        } />
        <Route path="d-finanzas" element={
          <ModuleRoute moduleId="finanzas" moduleType="direction">
            <Finanzas />
          </ModuleRoute>
        } />
        <Route path="d-informacion" element={
          <ModuleRoute moduleId="informacion" moduleType="direction">
            <Informacion />
          </ModuleRoute>
        } />
        <Route path="d-tecnologia" element={
          <ModuleRoute moduleId="tecnologia" moduleType="direction">
            <Tecnologia />
          </ModuleRoute>
        } />
        <Route path="wca-learn" element={
          <ModuleRoute moduleId="learn" moduleType="division">
            <WCALearn />
          </ModuleRoute>
        } />
        <Route path="wca-network" element={
          <ModuleRoute moduleId="network" moduleType="division">
            <WCANetwork />
          </ModuleRoute>
        } />
        <Route path="wca-labs" element={
          <ModuleRoute moduleId="labs" moduleType="division">
            <WCALabs />
          </ModuleRoute>
        } />
        <Route path="wca-impact" element={
          <ModuleRoute moduleId="impact" moduleType="division">
            <WCAImpact />
          </ModuleRoute>
        } />
      </Route>
      <Route path="*" element={<Navigate to="/general" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors closeButton />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
